/* Presentation helpers; the existing gallery and product form own selection. */
if (!customElements.get('pdp-thumbnail-rail')) {
  customElements.define('pdp-thumbnail-rail', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.product-media-thumbnails');
      this.buttons = this.querySelectorAll('[data-rail-direction]');
      if (!this.rail) return;
      this.onClick = (event) => {
        const button = event.target.closest('[data-rail-direction]');
        if (!button) return;
        const thumbnail = [...this.rail.children].find(thumb => thumb.getClientRects().length);
        const step = (thumbnail?.getBoundingClientRect().height || 64) + parseFloat(getComputedStyle(this.rail).rowGap || 0);
        this.rail.scrollBy({
          top: Number(button.dataset.railDirection) * step * Math.max(1, Math.floor(this.rail.clientHeight / step) - 1),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      };
      this.update = () => {
        this.rail.querySelectorAll('.product-media-thumbnail').forEach(thumb => {
          const button = thumb.querySelector('button');
          if (!button) return;
          button.setAttribute('aria-current', thumb.classList.contains('is-active') ? 'true' : 'false');
          button.removeAttribute('aria-haspopup');
        });
        this.buttons.forEach(button => {
          button.disabled = Number(button.dataset.railDirection) < 0
            ? this.rail.scrollTop <= 1
            : this.rail.scrollTop + this.rail.clientHeight >= this.rail.scrollHeight - 1;
        });
      };
      this.addEventListener('click', this.onClick);
      this.rail.addEventListener('scroll', this.update, { passive: true });
      this.observer = new ResizeObserver(() => {
        this.fitViewport();
        this.reveal(this.rail.querySelector('.is-active'));
      });
      this.observer.observe(this);
      this.observer.observe(this.rail);
      this.rail.querySelectorAll('.product-media-thumbnail').forEach(thumb => this.observer.observe(thumb));
      this.update();
    }
    fitViewport() {
      if (!matchMedia('(min-width: 750px)').matches) {
        this.style.removeProperty('--pdp-rail-viewport');
        return;
      }
      const thumbnails = [...this.rail.children].filter(thumb => thumb.getClientRects().length);
      if (!thumbnails.length) return;
      const style = getComputedStyle(this.rail);
      const gap = parseFloat(style.rowGap) || 0;
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const available = Math.max(0, this.clientHeight - 2 * parseFloat(style.top));
      const step = thumbnails[0].getBoundingClientRect().height + gap;
      const rows = Math.min(thumbnails.length, Math.max(1, Math.floor((available - padding + gap) / step)));
      // Whole rows avoid leaving a clipped sliver above the selected last image.
      const height = `${Math.min(available, rows * step - gap + padding).toFixed(2)}px`;
      if (this.style.getPropertyValue('--pdp-rail-viewport') !== height) {
        this.style.setProperty('--pdp-rail-viewport', height);
      }
    }
    reveal(thumbnail) {
      if (!this.rail || !thumbnail) return;
      const vertical = matchMedia('(min-width: 750px)').matches;
      if (!vertical) {
        this.update();
        return;
      }
      const railBounds = this.rail.getBoundingClientRect();
      const thumbBounds = thumbnail.getBoundingClientRect();
      const start = vertical ? 'top' : 'left';
      const end = vertical ? 'bottom' : 'right';
      let delta = 0;
      if (thumbBounds[start] < railBounds[start] + 2) {
        delta = thumbBounds[start] - railBounds[start] - 2;
      } else if (thumbBounds[end] > railBounds[end] - 2) {
        delta = thumbBounds[end] - railBounds[end] + 2;
      }
      if (delta) {
        // Scroll only this rail; scrollIntoView also moves the whole page.
        this.rail.scrollBy({ [start]: delta, behavior: 'instant' });
      }
      this.update();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.rail?.removeEventListener('scroll', this.update);
      this.observer?.disconnect();
    }
  });
}
